import { createApi } from '@reduxjs/toolkit/query/react';
import { generatePath } from 'react-router-dom';
import { baseQuery } from 'commons/src/api/baseQuery';
import { ORGANIZATION_HEALTH_TABLE_LENGTH } from '../constants';
import { BuildingHealthRowData } from '../models/buildingHealthModels';
import {
    OrganizationBuildingOverview,
    OrganizationBuildingResponse,
    OrganizationHealthApiResponse,
    OrganizationHealthResponse,
} from '../models/organizationHealthModels';

const convertApiResponseToOrganizationSummary = (
    apiResponse: OrganizationHealthApiResponse
): OrganizationBuildingOverview => ({
    locationId: apiResponse.locationId,
    locationName: apiResponse.locationName,
    hubStats: {
        total: apiResponse.totalHubs,
        offline: apiResponse.offlineHubs,
    },
    deviceStats: {
        total: apiResponse.totalDevices,
        offline: apiResponse.offlineDevices,
        lowBattery: apiResponse.lowBatteryDevices,
    },
});

type PartnerHealthStatusParams = {
    partnerManagedUserGroupId: string;
    userGroupId: string;
    locationId?: string;
    limit?: number;
    offset?: number;
    groupType?: string;
};

type BuildingListResponse = {
    totalBuildings: number;
    buildings: OrganizationHealthApiResponse[];
};

export const partnerHealthStatusApi = createApi({
    reducerPath: 'partnerHealthStatusApi',
    baseQuery,
    tagTypes: ['PartnerHealthStatus', 'PartnerBuildingData'],
    endpoints: builder => ({
        getPartnerHealthStatus: builder.query<
            OrganizationHealthResponse | OrganizationBuildingResponse,
            PartnerHealthStatusParams
        >({
            query: ({
                partnerManagedUserGroupId,
                userGroupId,
                locationId,
                limit = ORGANIZATION_HEALTH_TABLE_LENGTH,
                offset = 0,
                groupType = 'partner',
            }) => {
                const params = new URLSearchParams({
                    partnerManagedUserGroupId,
                    userGroupId,
                    groupType,
                    limit: String(limit),
                    offset: String(offset),
                });

                if (locationId) {
                    const baseUrl = generatePath('/partner/locations/:locationId/health-status/hubs-and-devices', {
                        locationId,
                    });
                    return {
                        url: `${baseUrl}?${params.toString()}`,
                        method: 'GET',
                    };
                }

                return {
                    url: `/partner/health-status?${params.toString()}`,
                    method: 'GET',
                };
            },
            transformResponse: (
                response: BuildingListResponse | OrganizationBuildingResponse,
                _meta,
                arg
            ): OrganizationHealthResponse | OrganizationBuildingResponse => {
                if (arg.locationId) {
                    const deviceResponse = response as OrganizationBuildingResponse;
                    const devicesWithLocationId = (deviceResponse.devices || []).map(device => {
                        const deviceWithLocation = device as BuildingHealthRowData & { locationId?: string };
                        return {
                            ...deviceWithLocation,
                            locationId:
                                'locationId' in deviceWithLocation && deviceWithLocation.locationId
                                    ? deviceWithLocation.locationId
                                    : arg.locationId,
                        } as BuildingHealthRowData & { locationId: string };
                    });
                    return {
                        totalDevices: deviceResponse.totalDevices || 0,
                        devices: devicesWithLocationId,
                    };
                }

                const buildingResponse = response as BuildingListResponse;
                if (buildingResponse?.buildings && Array.isArray(buildingResponse.buildings)) {
                    return {
                        totalLocations: buildingResponse.totalBuildings,
                        locations: buildingResponse.buildings.map(convertApiResponseToOrganizationSummary),
                    };
                }

                return { totalLocations: 0, locations: [] };
            },
            providesTags: (_result, _error, arg) =>
                arg.locationId ? [{ type: 'PartnerBuildingData', id: arg.locationId }] : ['PartnerHealthStatus'],
        }),
    }),
});

type PartnerDeviceSearchParams = {
    query: string;
    userGroupId: string;
};

export type DeviceSearchResult = {
    serialNumber: string;
    segmentName: string;
    locationId: string;
    locationName: string;
    userGroupId: string;
    userGroupName: string;
    spaceId?: string;
    spaceName?: string;
};

export const partnerDeviceSearchApi = createApi({
    reducerPath: 'partnerDeviceSearchApi',
    baseQuery,
    tagTypes: ['PartnerDeviceSearch'],
    endpoints: builder => ({
        searchPartnerDevices: builder.query<DeviceSearchResult[], PartnerDeviceSearchParams>({
            query: ({ query, userGroupId }) => {
                const params = new URLSearchParams({
                    q: query,
                    userGroupId,
                });
                return {
                    url: `/partner/devices/search?${params.toString()}`,
                    method: 'GET',
                };
            },
            providesTags: ['PartnerDeviceSearch'],
        }),
    }),
});

export const { useGetPartnerHealthStatusQuery } = partnerHealthStatusApi;
export const { useSearchPartnerDevicesQuery } = partnerDeviceSearchApi;
